import type { CategoryInfo, DeployResult, TrainParams, TrainingDevice } from '../types/annotator';

const BASE = '/annotator-api';

function getToken(): string | null {
  return localStorage.getItem('dmo_access_token');
}

export const CATEGORY_MODEL_MAP: Record<string, { dir: string; file: string }> = {
  cats: { dir: 'cat', file: 'best.pt' },
  foxcats: { dir: 'fox_cat', file: 'best.pt' },
  foxes: { dir: 'fox', file: 'best.pt' },
  pigs: { dir: 'pig', file: 'best.pt' },
  barcodes: { dir: 'coco', file: 'best.pt' },
  cartonlabels: { dir: 'coco', file: 'best.pt' },
};

export const DEFAULT_TRAIN_PARAMS: TrainParams = {
  category: '',
  model_size: 'm',
  epochs: 130,
  batch: 8,
  image_size: 640,
  val_split: 0.2,
  patience: 50,
  device: '0',
  lr: 0.01,
  resume: false,
  cls: 1.0,
  mosaic: 0.5,
  mixup: 0.05,
  copy_paste: 0.0,
  hsv_s: 0.5,
  erasing: 0.2,
  no_aug_epochs: 15,
  label: '',
  backend: 'libreyolo',
};

export async function fetchTrainingConfig(projectId: number): Promise<CategoryInfo> {
  const token = getToken();
  const res = await fetch(`${BASE}/projects/${projectId}/training/config`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export async function fetchTrainingDevices(projectId: number): Promise<{ devices: TrainingDevice[] }> {
  const token = getToken();
  const res = await fetch(`${BASE}/projects/${projectId}/training/devices`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export async function startTraining(projectId: number, params: TrainParams): Promise<{ run_id: string }> {
  const token = getToken();
  const res = await fetch(`${BASE}/projects/${projectId}/training/start`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ ...params, label: params.label.trim() || undefined, backend: params.backend || undefined }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.detail ?? `HTTP ${res.status}`);
  }
  return res.json();
}

export async function cancelTraining(projectId: number, runId: string): Promise<void> {
  const token = getToken();
  await fetch(`${BASE}/projects/${projectId}/training/${runId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
}

export async function deployModel(projectId: number, runId: string, category: string, deployPath?: string): Promise<DeployResult> {
  const token = getToken();
  const res = await fetch(`${BASE}/projects/${projectId}/training/deploy`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ run_id: runId, category, ...(deployPath ? { deploy_path: deployPath } : {}) }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.detail ?? `HTTP ${res.status}`);
  }
  return res.json();
}

export async function deployLatestModel(projectId: number, category: string, deployPath?: string): Promise<DeployResult> {
  const token = getToken();
  const res = await fetch(`${BASE}/projects/${projectId}/training/deploy-latest`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ category, ...(deployPath ? { deploy_path: deployPath } : {}) }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.detail ?? `HTTP ${res.status}`);
  }
  return res.json();
}

export interface TrainingRunSummary {
  run_id: string;
  category: string;
  label: string | null;
  status: 'running' | 'done' | 'error' | 'cancelled' | 'interrupted';
  started_at: string | null;
  finished_at: string | null;
  save_dir: string | null;
  best_map50: number | null;
  best_epoch: number | null;
  returncode: number | null;
  /** 'output' = recovered from the training output folder (runs from before log history was kept) */
  source?: 'output';
}

export async function fetchTrainingRuns(projectId: number): Promise<TrainingRunSummary[]> {
  const token = getToken();
  const res = await fetch(`${BASE}/projects/${projectId}/training/runs`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()).runs;
}

export async function fetchTrainingRunLog(projectId: number, runId: string): Promise<TrainingRunSummary & { lines: string[] }> {
  const token = getToken();
  const res = await fetch(`${BASE}/projects/${projectId}/training/runs/${encodeURIComponent(runId)}/log`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.detail ?? `HTTP ${res.status}`);
  }
  return res.json();
}

export async function downloadBestModel(projectId: number, category?: string): Promise<void> {
  const token = getToken();
  const qs = category ? `?category=${encodeURIComponent(category)}` : '';
  const res = await fetch(`${BASE}/projects/${projectId}/training/download-best${qs}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.detail ?? `HTTP ${res.status}`);
  }
  const filename = /filename="?([^";]+)"?/.exec(res.headers.get('Content-Disposition') ?? '')?.[1] ?? 'best.zip';
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function streamTrainingLogs(
  projectId: number,
  runId: string,
  onMessage: (msg: Record<string, unknown>) => void,
  onDone: () => void,
  onError: (err: string) => void,
): AbortController {
  const controller = new AbortController();
  const token = getToken();
  let receivedTerminalEvent = false;

  (async () => {
    try {
      const res = await fetch(`${BASE}/projects/${projectId}/training/${runId}/stream`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      });
      if (!res.ok || !res.body) {
        onError(`Stream failed: HTTP ${res.status}`);
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop() ?? '';
        for (const part of parts) {
          for (const line of part.split('\n')) {
            if (line.startsWith('data: ')) {
              try {
                const msg = JSON.parse(line.slice(6));
                onMessage(msg);
                if (msg.type === 'done' || msg.type === 'error' || msg.type === 'cancelled') {
                  receivedTerminalEvent = true;
                  onDone();
                  return;
                }
              } catch {
                // skip malformed JSON
              }
            }
          }
        }
      }
      if (!receivedTerminalEvent) {
        onError('Stream disconnected unexpectedly — training may still be running');
      } else {
        onDone();
      }
    } catch (err: unknown) {
      if ((err as { name?: string }).name !== 'AbortError') {
        onError(String(err));
      }
    }
  })();

  return controller;
}
